// Public discovery taxonomy. These labels describe recorded notes and accords, not product relationships.
export const noteHubs = [
  'oud', 'vanilla', 'cherry', 'rose', 'coconut', 'amber', 'musk', 'jasmine',
  'fig', 'pistachio', 'tobacco', 'leather',
] as const;

export const familyHubs = {
  gourmand: ['gourmand', 'sweet', 'vanilla', 'caramel', 'chocolate'],
  fresh: ['fresh', 'aquatic', 'green', 'clean', 'citrus'],
  floral: ['floral', 'rose', 'jasmine', 'violet', 'iris', 'white floral'],
  citrus: ['citrus', 'bergamot', 'orange', 'lemon', 'grapefruit'],
  woody: ['woody', 'sandalwood', 'cedar', 'oud'],
  amber: ['amber', 'resinous', 'balsamic', 'warm spicy'],
} as const;

export const scentPresets = [
  { id: 'fresh-clean', name: 'Bright citrus', description: 'Fresh · citrus', families: ['fresh', 'citrus'], notes: [] },
  { id: 'soft-floral', name: 'Soft rose', description: 'Floral · rose', families: ['floral'], notes: ['rose'] },
  { id: 'warm-cosy', name: 'Warm amber', description: 'Amber · vanilla', families: ['amber'], notes: ['vanilla'] },
  { id: 'woody-grounded', name: 'Deep woods', description: 'Woody · oud', families: ['woody'], notes: ['oud'] },
] as const;

export type DiscoveryKind = 'notes' | 'families';
export type DiscoveryItem = {
  slug: string;
  name: string;
  concentration: string;
  brand: { name: string } | null;
  keyNotes: string[];
  gender: string;
  accords: { name: string; intensity: number }[];
  longevity: string | null;
  matchScore: number;
  price?: number | null;
};
export type DiscoveryHub = { kind: DiscoveryKind; slug: string; items: DiscoveryItem[]; total: number; page?: number; pageSize?: number; concentrations?: string[] };
/** How many published fragrances match each hub, keyed by hub slug. */
export type DiscoveryCounts = { notes: Record<string, number>; families: Record<string, number> };
export type DiscoveryFilters = { gender?: string; concentration?: string; season?: string; budget?: number };

export function discoveryPath(kind: DiscoveryKind, slug: string) {
  return `/${kind}/${encodeURIComponent(slug)}`;
}

export function discoveryMatch(kind: DiscoveryKind, slug: string, item: Pick<DiscoveryItem, 'keyNotes' | 'accords'>) {
  const terms: readonly string[] = kind === 'notes' ? [slug] : Object.hasOwn(familyHubs, slug) ? familyHubs[slug as keyof typeof familyHubs] : [];
  if (!terms.length) return 0;
  const accord = item.accords.find(row => terms.some(term => row.name.toLowerCase().includes(term)));
  const noteIndex = item.keyNotes.findIndex(note => terms.some(term => note.toLowerCase().includes(term)));
  return Math.max(accord ? accord.intensity * 10 : 0, noteIndex < 0 ? 0 : 25 - noteIndex);
}

export const seasonTerms: Record<string, string[]> = {
  spring: ['floral', 'green', 'fresh', 'rose'], summer: ['citrus', 'fresh', 'aquatic', 'coconut'],
  autumn: ['woody', 'amber', 'spicy', 'tobacco'], winter: ['amber', 'vanilla', 'oud', 'gourmand'],
};
export function matchesSeason(item: Pick<DiscoveryItem, 'keyNotes' | 'accords'>, season: string) {
  if (!season) return true;
  const values = [...item.keyNotes, ...item.accords.map(accord => accord.name)].map(value => value.toLowerCase());
  return (seasonTerms[season] ?? []).some(term => values.some(value => value.includes(term)));
}
